import { useState, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";
import { has, isArray } from "lodash";

import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { socketConnection } from "../../services/socket";
import noopSocket, { createNoopSocket } from "../../services/noopSocket";
// import { useDate } from "../../hooks/useDate";
import moment from "moment";
import { syncNativeAppAuth, isNativeApp } from "../../utils/nativeAppBridge";
import { prefetchPlanFlags } from "../../utils/planFeatureCache";

const createSocketForUser = (userData) => {
  if (!userData?.id || !userData?.companyId) return createNoopSocket();
  return socketConnection({ user: userData });
};

const useAuth = () => {
  const history = useHistory();
  const [isAuth, setIsAuth] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState({});
  const [socket, setSocket] = useState(noopSocket);
 

  api.interceptors.request.use(
    (config) => {
      // Buscar token en localStorage primero, luego en sessionStorage
      let token = localStorage.getItem("token");
      if (!token) {
        token = sessionStorage.getItem("token");
      }
      if (token) {
        config.headers["Authorization"] = `Bearer ${JSON.parse(token)}`;
      }
      return config;
    },
    (error) => {
      Promise.reject(error);
    }
  );

  api.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
      const originalRequest = error.config;
      if (error?.response?.status === 403 && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          const { data } = await api.post("/auth/refresh_token");
          if (data) {
            localStorage.setItem("token", JSON.stringify(data.token));
            api.defaults.headers.Authorization = `Bearer ${data.token}`;
          }
          return api(originalRequest);
        } catch (refreshError) {
          localStorage.removeItem("token");
          sessionStorage.removeItem("token");
          api.defaults.headers.Authorization = undefined;
          setIsAuth(false);
          return Promise.reject(refreshError);
        }
      }
      if (error?.response?.status === 401) {
        localStorage.removeItem("token");
        sessionStorage.removeItem("token");
        api.defaults.headers.Authorization = undefined;
        setIsAuth(false);
      }
      return Promise.reject(error);
    }
  );

  useEffect(() => {
    // Buscar token en localStorage primero, luego en sessionStorage
    let token = localStorage.getItem("token");
    if (!token) {
      token = sessionStorage.getItem("token");
    }
    (async () => {
      try {
        if (token) {
          const { data } = await api.post("/auth/refresh_token");
          api.defaults.headers.Authorization = `Bearer ${data.token}`;
          setIsAuth(true);
          setUser(data.user);
          setSocket(createSocketForUser(data.user));

          const rememberMe = localStorage.getItem("rememberMe");
          if (rememberMe === "true") {
            localStorage.setItem("token", JSON.stringify(data.token));
          } else {
            sessionStorage.setItem("token", JSON.stringify(data.token));
          }
          syncNativeAppAuth(data.token);
          if (data.user?.companyId) {
            prefetchPlanFlags(data.user.companyId);
          }
        }
      } catch (err) {
        console.log("Token refresh failed:", err);
        if (err.response?.status === 401 || err.response?.status === 403) {
          localStorage.removeItem("token");
          sessionStorage.removeItem("token");
        }
        if (err.response?.status !== 401) {
          toastError(err);
        }
      } finally {
        setLoading(false);
        if (typeof window.hideSplashScreen === "function") {
          window.hideSplashScreen();
        }
      }
    })();
  }, []);

  useEffect(() => {
    const safetyTimer = window.setTimeout(() => {
      setLoading(false);
      if (typeof window.hideSplashScreen === "function") {
        window.hideSplashScreen();
      }
    }, 12000);

    return () => window.clearTimeout(safetyTimer);
  }, []);

  useEffect(() => {
    if (!user?.id || !socket || typeof socket.on !== "function") {
      return undefined;
    }

    const onCompanyUser = (data) => {
      if (data.action === "update" && data.user.id === user.id) {
        setUser(data.user);
      }
    };

    socket.on(`company-${user.companyId}-user`, onCompanyUser);

    return () => {
      socket.off(`company-${user.companyId}-user`, onCompanyUser);
    };
  }, [user?.id, user?.companyId, socket]);

  const handleLogin = async (userData) => {
    setLoading(true);

    try {
      const { data } = await api.post("/auth/login", userData);
      
      // Manejar "recordarme"
      if (userData.remember || isNativeApp()) {
        localStorage.setItem("rememberMe", "true");
        localStorage.setItem("rememberedEmail", userData.email);
      } else {
        // Si no está activado, usar sessionStorage para sesión temporal
        localStorage.removeItem("rememberMe");
        localStorage.removeItem("rememberedEmail");
        sessionStorage.setItem("token", JSON.stringify(data.token));
      }
      const {
        user: { company },
      } = data;

      if (has(company, "companieSettings") && isArray(company.companieSettings[0])) {
        const setting = company.companieSettings[0].find(
          (s) => s.key === "campaignsEnabled"
        );
        if (setting && setting.value === "true") {
          localStorage.setItem("cshow", null); //regra pra exibir campanhas
        }
      }

      if (has(company, "companieSettings") && isArray(company.companieSettings[0])) {
        const setting = company.companieSettings[0].find(
          (s) => s.key === "sendSignMessage"
        );

        const signEnable = setting.value === "enable";

        if (setting && setting.value === "enabled") {
          localStorage.setItem("sendSignMessage", signEnable); //regra pra exibir campanhas
        }
      }
      localStorage.setItem("profileImage", data.user.profileImage); //regra pra exibir imagem contato

      moment.locale('pt-br');
      let dueDate;
      if (data.user.company.id === 1) {
        dueDate = '2999-12-31T00:00:00.000Z'
      } else {
        dueDate = data.user.company.dueDate;
      }
      const hoje = moment(moment()).format("DD/MM/yyyy");
      const vencimento = moment(dueDate).format("DD/MM/yyyy");

      var diff = moment(dueDate).diff(moment(moment()).format());

      var before = moment(moment().format()).isBefore(dueDate);
      var dias = moment.duration(diff).asDays();

      if (before === true) {
        // Guardar token según si "recordarme" está activado
        if (userData.remember || isNativeApp()) {
          localStorage.setItem("token", JSON.stringify(data.token));
        } else {
          sessionStorage.setItem("token", JSON.stringify(data.token));
        }
        // localStorage.setItem("public-token", JSON.stringify(data.user.token));
        // localStorage.setItem("companyId", companyId);
        // localStorage.setItem("userId", id);
        localStorage.setItem("companyDueDate", vencimento);
        api.defaults.headers.Authorization = `Bearer ${data.token}`;
        setUser(data.user);
        setSocket(createSocketForUser(data.user));
        setIsAuth(true);
        syncNativeAppAuth(data.token);
        if (data.user?.companyId) {
          prefetchPlanFlags(data.user.companyId);
        }
        toast.success(i18n.t("auth.toasts.success"));
        if (Math.round(dias) < 5) {
          toast.warn(`Su suscripción vence en ${Math.round(dias)} ${Math.round(dias) === 1 ? 'día' : 'días'} `);
        }

        // // Atraso para garantir que o cache foi limpo
        // setTimeout(() => {
        //   window.location.reload(true); // Recarregar a página
        // }, 1000);

        history.push("/tickets");
        setLoading(false);
      } else {
        // localStorage.setItem("companyId", companyId);
        api.defaults.headers.Authorization = `Bearer ${data.token}`;
        setUser(data.user);
        setSocket(createSocketForUser(data.user));
        setIsAuth(true);
        syncNativeAppAuth(data.token);
        toastError(`¡Ups! Su suscripción venció el ${vencimento}.
¡Contáctese con el Soporte para más información! `);
        history.push("/financeiro-aberto");
        setLoading(false);
      }

    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    setLoading(true);

    try {
      // socket.disconnect();
      await api.delete("/auth/logout");
      setIsAuth(false);
      setUser({});
      setSocket(createNoopSocket());
      localStorage.removeItem("token");
      sessionStorage.removeItem("token");
      localStorage.removeItem("rememberMe");
      localStorage.removeItem("rememberedEmail");
      localStorage.removeItem("cshow");
      // localStorage.removeItem("public-token");
      api.defaults.headers.Authorization = undefined;
      setLoading(false);
      history.push("/login");
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const getCurrentUserInfo = async () => {
    try {
      const { data } = await api.get("/auth/me");
      console.log(data)
      return data;
    } catch (_) {
      return null;
    }
  };

  return {
    isAuth,
    user,
    loading,
    handleLogin,
    handleLogout,
    getCurrentUserInfo,
    socket
  };
};

export default useAuth;
